import React, { useCallback, useMemo, useState } from 'react';
import { getIcon } from '@flybits/design-system';
import { ZoneConfig } from 'pages/Zones/types';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import ModuleListItem from './ModuleListItem/ModuleListItem';
import './ModulePositionManager.scss';

type TModulePositionManager = {
  isOpen: boolean;
  onClose: () => void;
  zoneId: string;
  zoneConfig: ZoneConfig;
  setZoneConfig: React.Dispatch<React.SetStateAction<ZoneConfig | undefined>>;
};

const MAIN_CLASS = 'module-position-manager';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CLASSES = {
  CATEGORIES: `${MAIN_CLASS}__categories`,
  CATEGORY_CONTENT: `${MAIN_CLASS}__category-content`,
  LABELS_CONTENT: `${MAIN_CLASS}__labels-content`,
  PINNED_CONTENT: `${MAIN_CLASS}__pinned-content`,
  PLACEHOLDER: `${MAIN_CLASS}__placeholder`,
};

const ModulePositionManager: React.FC<TModulePositionManager> = ({
  isOpen,
  zoneId,
  zoneConfig,
  onClose,
  setZoneConfig,
}) => {
  const [initialZoneConfig] = useState(zoneConfig);
  const [currentDraggingIndex, setCurrentDraggingIndex] = useState<number>();
  const zoneIndex = zoneConfig.zones.findIndex((zone) => zone.id === zoneId);

  const reset = useCallback(() => setZoneConfig(initialZoneConfig), [initialZoneConfig, setZoneConfig]);

  const handleCancel = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleChangePosition = (oldIndex: number, newIndex: number) => {
    const _modules = [...zoneConfig.zones[zoneIndex].modules];
    [_modules[oldIndex], _modules[newIndex]] = [_modules[newIndex], _modules[oldIndex]];

    const _zoneConfig = { ...zoneConfig };
    _zoneConfig.zones[zoneIndex].modules = _modules;
    setZoneConfig(_zoneConfig);
  };

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: getIcon('itemsList', {}) as JSX.Element,
        goBackTitle: 'Module Position',
        goBackSubTitle: 'Arrange the modules within this zone in the desired order.',
        goBackActionHandler: onClose,
      },
      footerInfo: {
        primaryActionText: 'Save',
        primaryActionHandler: onClose,
        secondaryActionText: 'Cancel',
        secondaryActionHandler: handleCancel,
      },
      size: 'medium',
      className: MAIN_CLASS,
    }),
    [isOpen, onClose, handleCancel],
  );

  return (
    <SlidingSidePanel {...slidingSidePanelProps}>
      <div>
        {zoneConfig?.zones[zoneIndex]?.modules?.map((module, index) => (
          <ModuleListItem
            key={module.id}
            index={index + 1}
            listLength={zoneConfig.zones[zoneIndex].modules.length}
            name={module.name}
            onDragStart={setCurrentDraggingIndex}
            onDragEnd={() => setCurrentDraggingIndex(undefined)}
            onChangePosition={(oldIndex, newIndex) => handleChangePosition(oldIndex - 1, newIndex - 1)}
            showDropzone={
              currentDraggingIndex !== undefined &&
              currentDraggingIndex !== index + 1 &&
              currentDraggingIndex !== index + 2
            }
          />
        )) || null}
      </div>
    </SlidingSidePanel>
  );
};

export default ModulePositionManager;
